// client/pages/P2PRegister.tsx - FIXED (No Solana imports needed)

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

export default function P2PRegister() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    fullName: '',
    phoneNumber: '',
    email: '',
    wallet: '',
    preferredPaymentMethod: 'Bank Transfer',
    bankName: '',
    accountNumber: '',
    accountName: '',
    easyPaisaNumber: '',
    jazzCashNumber: ''
  });

  const handleSubmit = async () => {
    if (!formData.fullName || !formData.phoneNumber || !formData.email || !formData.wallet) {
      toast({ title: 'ERROR', description: 'Please fill all required fields', variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('/api/p2p/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      if (data.success) {
        toast({ title: 'SUCCESS', description: 'P2P registration completed!' });
        navigate('/p2p');
      } else {
        toast({ title: 'ERROR', description: data.error || 'Registration failed', variant: 'destructive' });
      }
    } catch (error) {
      console.error('Registration error:', error);
      toast({ title: 'ERROR', description: 'Registration failed', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white p-4">
      <div className="flex items-center gap-2 mb-6">
        <button onClick={() => navigate('/p2p')} className="text-gray-400 hover:text-white text-[11px] uppercase font-bold">
          BACK
        </button>
        <h1 className="text-[11px] font-bold uppercase tracking-wider text-white">P2P REGISTRATION</h1>
      </div>

      <div className="w-full max-w-2xl mx-auto">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
            <span className="text-2xl">📝</span>
          </div>
          <div>
            <h1 className="text-[11px] font-bold uppercase tracking-wider text-white">COMPLETE YOUR P2P PROFILE</h1>
            <p className="text-[11px] uppercase text-gray-400">Required to start P2P trading</p>
          </div>
        </div>

        <div className="w-full space-y-4">
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-gray-400 block mb-2">FULL NAME *</label>
            <input
              type="text"
              value={formData.fullName}
              onChange={(e) => setFormData({...formData, fullName: e.target.value})}
              className="w-full bg-gray-800 rounded-lg px-4 py-3 text-white text-base focus:outline-none focus:ring-2 focus:ring-yellow-500/50"
              placeholder="Enter your full name"
            />
          </div>

          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-gray-400 block mb-2">PHONE NUMBER *</label>
            <input
              type="text"
              value={formData.phoneNumber}
              onChange={(e) => setFormData({...formData, phoneNumber: e.target.value})}
              className="w-full bg-gray-800 rounded-lg px-4 py-3 text-white text-base focus:outline-none focus:ring-2 focus:ring-yellow-500/50"
              placeholder="Enter your phone number"
            />
          </div>

          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-gray-400 block mb-2">EMAIL *</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              className="w-full bg-gray-800 rounded-lg px-4 py-3 text-white text-base focus:outline-none focus:ring-2 focus:ring-yellow-500/50"
              placeholder="Enter your email"
            />
          </div>

          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-gray-400 block mb-2">SOLANA WALLET ADDRESS *</label>
            <input
              type="text"
              value={formData.wallet}
              onChange={(e) => setFormData({...formData, wallet: e.target.value})}
              className="w-full bg-gray-800 rounded-lg px-4 py-3 text-white text-base focus:outline-none focus:ring-2 focus:ring-yellow-500/50"
              placeholder="Enter your Solana wallet address"
            />
            <p className="text-[10px] uppercase text-gray-500 mt-1">Enter your Solana wallet public key</p>
          </div>

          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-gray-400 block mb-2">PREFERRED PAYMENT METHOD</label>
            <div className="flex flex-wrap gap-2">
              {['Bank Transfer', 'EasyPaisa', 'JazzCash'].map(method => (
                <button
                  key={method}
                  onClick={() => setFormData({...formData, preferredPaymentMethod: method})}
                  className={cn(
                    "px-4 py-2 rounded-lg text-[11px] font-bold uppercase tracking-wider transition-colors",
                    formData.preferredPaymentMethod === method
                      ? "bg-white/10 text-white"
                      : "bg-gray-800 text-gray-400 hover:bg-gray-700"
                  )}
                >
                  {method}
                </button>
              ))}
            </div>
          </div>

          {formData.preferredPaymentMethod === 'Bank Transfer' && (
            <>
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-gray-400 block mb-2">BANK NAME</label>
                <input
                  type="text"
                  value={formData.bankName}
                  onChange={(e) => setFormData({...formData, bankName: e.target.value})}
                  className="w-full bg-gray-800 rounded-lg px-4 py-3 text-white text-base focus:outline-none focus:ring-2 focus:ring-yellow-500/50"
                  placeholder="Enter your bank name"
                />
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-gray-400 block mb-2">ACCOUNT NUMBER</label>
                <input
                  type="text"
                  value={formData.accountNumber}
                  onChange={(e) => setFormData({...formData, accountNumber: e.target.value})}
                  className="w-full bg-gray-800 rounded-lg px-4 py-3 text-white text-base focus:outline-none focus:ring-2 focus:ring-yellow-500/50"
                  placeholder="Enter your account number"
                />
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-gray-400 block mb-2">ACCOUNT HOLDER NAME</label>
                <input
                  type="text"
                  value={formData.accountName}
                  onChange={(e) => setFormData({...formData, accountName: e.target.value})}
                  className="w-full bg-gray-800 rounded-lg px-4 py-3 text-white text-base focus:outline-none focus:ring-2 focus:ring-yellow-500/50"
                  placeholder="Enter account holder name"
                />
              </div>
            </>
          )}

          {formData.preferredPaymentMethod === 'EasyPaisa' && (
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wider text-gray-400 block mb-2">EASYPAISA NUMBER</label>
              <input
                type="text"
                value={formData.easyPaisaNumber}
                onChange={(e) => setFormData({...formData, easyPaisaNumber: e.target.value})}
                className="w-full bg-gray-800 rounded-lg px-4 py-3 text-white text-base focus:outline-none focus:ring-2 focus:ring-yellow-500/50"
                placeholder="Enter your EasyPaisa number"
              />
            </div>
          )}

          {formData.preferredPaymentMethod === 'JazzCash' && (
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wider text-gray-400 block mb-2">JAZZCASH NUMBER</label>
              <input
                type="text"
                value={formData.jazzCashNumber}
                onChange={(e) => setFormData({...formData, jazzCashNumber: e.target.value})}
                className="w-full bg-gray-800 rounded-lg px-4 py-3 text-white text-base focus:outline-none focus:ring-2 focus:ring-yellow-500/50"
                placeholder="Enter your JazzCash number"
              />
            </div>
          )}

          <div className="flex gap-3 mt-6">
            <button onClick={() => navigate('/p2p')} className="flex-1 bg-transparent text-gray-400 hover:bg-gray-800 py-3 text-[11px] font-bold uppercase tracking-wider rounded-lg">
              CANCEL
            </button>
            <button onClick={handleSubmit} disabled={loading} className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-black font-bold py-3 text-[11px] uppercase tracking-wider rounded-lg disabled:opacity-50">
              {loading ? <div className="w-5 h-5 animate-spin mx-auto">●</div> : 'REGISTER'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
